import React from "react";
import { Link } from "react-router-dom";
import "./what-is-asdry.css";

const WhatIsASDRY: React.FC = () => {
  return (
    <div className="page">
      <div className="page-container">
        <header className="page-header">
          <h1 className="page-title">What is ASDRY?</h1>
          <p className="page-intro">

            ASDRY is an environmental preparation intelligence system that translates
            atmospheric conditions into structured operational insight.
          </p>
        </header>

        <section className="content-section">
          <p>
            Weather forecasts describe atmospheric conditions, but they do not
            directly explain how those conditions will affect preparation,
            effort, decisions, or outcomes. ASDRY is a framework designed to
            bridge that gap by translating environmental conditions into
            structured preparation insight.
          </p>

          <p>
            The system focuses on understanding how environmental conditions
            influence real‑world activity. Instead of simply presenting weather
            variables, the ASDRY model evaluates how those variables interact
            and what they mean for the activity taking place.
          </p>
        </section>

        <section className="content-section">
          <h2>The Problem</h2>
          <p>
            Forecasts typically report variables such as temperature, wind,
            humidity and precipitation. However, these values alone do not
            explain how conditions will actually affect participants,
            volunteers, infrastructure or operations.
          </p>

          <p>
            For example, a temperature of 5°C may appear manageable on a
            forecast, but when combined with wind exposure and damp surfaces it
            may significantly affect comfort, traction and preparation
            decisions. ASDRY attempts to interpret these conditions in terms of
            practical impact.
          </p>
        </section>

        <section className="content-section">
          <h2>The ASDRY Approach</h2>
          <p>
            The ASDRY engine evaluates environmental factors and translates
            them into a structured preparation signal. Rather than presenting
            isolated variables, the system evaluates environmental interactions
            and converts them into a simple preparation indicator known as the
            ASDRY Environmental Readiness Signal (AERS).
          </p>

          <p>
            The signal represents the level of environmental preparation that
            may be required for a specific activity.
          </p>
        </section>

        <section className="content-section">
          <h2>Example</h2>
          <p>
            For an outdoor running event, conditions such as low temperature,
            moderate wind, high humidity and damp surfaces may combine to
            produce a moderate preparation level. This can highlight
            considerations such as clothing, traction awareness, volunteer
            exposure and operational adjustments.
          </p>
        </section>

        <section className="content-section">
          <h2>How the ASDRY Model Interprets Conditions</h2>

          <p>
            At a high level the system follows a simple interpretation chain.
            Environmental data is evaluated, interactions between factors are
            considered, and the result is translated into preparation insight.
          </p>

          <div className="content-diagram">
            <div className="diagram-step">
              Environmental Inputs
              <div className="diagram-sub">
                temperature<br/>
                wind<br/>
                humidity<br/>
                precipitation
              </div>
            </div>

            <div className="diagram-arrow">↓</div>

            <div className="diagram-step">
              ASDRY Environmental Modelling
              <div className="diagram-sub">
                factor scoring<br/>
                interaction modelling<br/>
                surface context
              </div>
            </div>

            <div className="diagram-arrow">↓</div>

            <div className="diagram-step">
              AERS
              <div className="diagram-sub">
                ASDRY Environmental Readiness Signal
              </div>
            </div>

            <div className="diagram-arrow">↓</div>

            <div className="diagram-step">
              Preparation Insight
              <div className="diagram-sub">
                participant awareness<br/>
                operational considerations
              </div>
            </div>
          </div>

          <p>
            This page explains the concept at a high level. Practical examples
            of the system in use are available through the{" "}
            <Link to="/blog">Field Reports</Link>. Demonstrations of how the
            model is applied can also be explored on the{" "}
            <Link to="/demo">Showcase</Link> page.
          </p>
        </section>

        <section className="content-section">
          <h2>Applications</h2>
          <p>Environmental preparation intelligence can apply across a range of contexts:</p>

          <ul>
            <li>Sport and endurance events</li>
            <li>Outdoor operations and logistics</li>
            <li>Infrastructure and construction planning</li>
            <li>Public outdoor activity and recreation</li>
          </ul>
          <p>
            Practical demonstrations of these applications can be explored on the{" "}
            <Link to="/demo">Showcase</Link> page. Organisations interested
            in applying environmental preparation intelligence operationally can
            find more information on the{" "}
            <Link to="/business">Business</Link> page, while developers can
            explore technical integration through the{" "}
            <Link to="/api">API</Link> page. Additional background on the
            project and its development can be found on the{" "}
            <Link to="/about">About</Link> page.
          </p>
        </section>

        <section className="content-section">
          <h2>Field Testing</h2>
          <p>
            ASDRY is currently being tested through environmental field reports
            analysing real‑world events under varying conditions. These reports
            examine forecast inputs, environmental exposure and observed
            outcomes to refine how environmental signals are interpreted.
            You can read the latest observations in the{" "}
            <Link to="/blog">Field Reports archive</Link>.
          </p>
        </section>

        <section className="content-section">
          <h2>Learn More</h2>
          <p>
            To explore the ASDRY system further, you can view practical
            demonstrations on the <Link to="/demo">Showcase</Link> page,
            read real-world observations in the{" "}
            <Link to="/blog">Field Reports</Link>, learn about operational
            applications on the <Link to="/business">Business</Link> page,
            or explore technical integration through the{" "}
            <Link to="/api">API</Link> page.
          </p>

          <p>
            If you would like to learn more about the project or get in touch,
            visit the <Link to="/about">About</Link> page or use the{" "}
            <Link to="/contact">Contact</Link> page.
          </p>
        </section>

        <section className="content-section">
          <h2>Summary</h2>
          <p>
            ASDRY aims to move beyond simple weather forecasts by focusing on
            how environmental conditions influence real-world activity. By
            translating environmental information into preparation insight, the
            system helps improve awareness and decision-making in environments
            where conditions matter.
          </p>
        </section>
      </div>
    </div>
  );
};

export default WhatIsASDRY;